import { checkExactMathAnswer, exactAnswerPreview } from "../../../../utils/exactMathAnswer";
import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

const choose = values => values[Math.floor(Math.random() * values.length)];
const TYPES = ["FractionRootProductTask", "RootDecimalProductTask", "RootMixedDecimalProductTask", "CubeRootProductTask", "CubeRootDecimalProductTask", "CubeRootMixedProductTask", "RootDecimalQuotientTask", "CubeRootQuotientTask", "RootPowerTask", "RootPowerProductTask"];
function gcd(a, b) { while (b) [a, b] = [b, a % b]; return Math.abs(a) || 1; }
function fraction(num, den = 1) { const g = gcd(num, den); return { num: num / g, den: den / g }; }
const frac = x => x.den === 1 ? String(x.num) : `\\frac{${x.num}}{${x.den}}`;
const decimal = x => String(x.num / x.den).replace(".", "{,}");
const mixed = x => `${Math.floor(x.num / x.den)}\\frac{${x.num % x.den}}{${x.den}}`;
const root = (degree, text) => degree === 3 ? `\\sqrt[3]{${text}}` : `\\sqrt{${text}}`;

export function generateTask(options = {}) {
  const type = TYPES.includes(options.type) ? options.type : TYPES[0];
  let degree = type.startsWith("Cube") ? 3 : 2;
  let left, right, leftLatex, rightLatex, result, quotient = type.includes("Quotient"), power = type.includes("Power");
  if (options.basic === true && type === TYPES[0]) {
    const a=choose([2,3,4]),n=choose([2,3,5]);
    left=fraction(a*a*n);right=fraction(n);result=fraction(a*n);
  } else if (options.basic === true && type === TYPES[7]) {
    const a=choose([2,3,4]),n=choose([2,3]);
    left=fraction(a*a*a*n);right=fraction(n);result=fraction(a);
  } else if (type === TYPES[0]) {
    const candidates = [];
    for (const n of [3, 5, 6, 7, 10, 12, 15, 18]) {
      for (const num of [2, 3, 4, 5]) {
        for (const den of [1, 2, 3, 4]) {
          const radicand = fraction(num ** 2, n * den ** 2);
          if (num !== den && radicand.num > 1 && radicand.den > 1) {
            candidates.push({ left: fraction(n), right: radicand, result: fraction(num, den) });
          }
        }
      }
    }
    const picked = choose(candidates);
    left = picked.left; right = picked.right; result = picked.result;
  } else if (type === TYPES[1]) {
    const n = choose([5, 10, 20, 25, 50]), answer = choose([2, 3, 4]);
    left = fraction(answer ** 2, n); right = fraction(n); result = fraction(answer);
    leftLatex = decimal(left);
  } else if (type === TYPES[2]) {
    const row = choose([[6, 5, 3, 10, 3, 5], [9, 4, 4, 25, 3, 5], [5, 4, 1, 5, 1, 2], [8, 5, 2, 5, 4, 5], [12, 5, 3, 5, 6, 5], [27, 10, 3, 10, 9, 10]]);
    const scale = choose([1, 2, 3].filter(n => (row[0] * n ** 2) % row[1] !== 0));
    left = fraction(row[0] * scale ** 2, row[1]); right = fraction(row[2], row[3]); result = fraction(row[4] * scale, row[5]);
    leftLatex = left.den === 1 ? frac(left) : mixed(left); rightLatex = decimal(right);
  } else if (type === TYPES[3]) {
    const n = choose([2, 3, 5, 6, 7]);
    left = fraction(n); right = fraction(n ** 2); result = fraction(n);
  } else if (type === TYPES[4]) {
    const answer = choose([2, 3, 4, 5, 6]), den = choose([100, 1000]);
    left = fraction(answer ** 3, den); right = fraction(den); result = fraction(answer);
    leftLatex = decimal(left);
  } else if (type === TYPES[5]) {
    const row = choose([[2, 9, 4, 3, 2, 3], [3, 16, 9, 4, 3, 4], [1, 100, 27, 10, 3, 10]]);
    const scale = choose([1, 2, 3, 4].filter(n => (row[2] * n ** 3) % row[3] !== 0));
    left = fraction(row[0], row[1]); right = fraction(row[2] * scale ** 3, row[3]);
    result = fraction(row[4] * scale, row[5]);
    rightLatex = right.den === 1 ? frac(right) : mixed(right);
  } else if (type === TYPES[6]) {
    const n = choose([1, 2, 3, 4, 6, 7, 9]), den = choose([10, 20]);
    left = fraction(n ** 2, 10); right = fraction(den ** 2 / 10); result = fraction(n, den);
    leftLatex = decimal(left);
  } else if (type === TYPES[7]) {
    const answer = choose([5, 10, 15, 20]), divisor = choose([2, 5, 10]);
    left = fraction(answer ** 3 * divisor); right = fraction(divisor); result = fraction(answer);
  } else if (type === TYPES[8]) {
    const a = choose([2, 3, 4, 5, 6, 7]), exponent = choose([2, 4, 6]);
    left = fraction(a ** exponent); result = fraction(a ** (exponent / 2));
    leftLatex = `${a}^{${exponent}}`;
  } else {
    const a = choose([2, 3, 4, 5]), b = choose([2, 3, 4].filter(n => n !== a));
    degree = choose([2, 3]);
    left = fraction(a ** degree * b ** (2 * degree)); result = fraction(a * b ** 2);
    leftLatex = `${a}^{${degree}}\\cdot ${b}^{${2 * degree}}`;
  }
  leftLatex = leftLatex || frac(left);
  rightLatex = rightLatex || (right ? frac(right) : "");
  const expressionLatex = power ? root(degree, leftLatex) : quotient
    ? `\\frac{${root(degree, leftLatex)}}{${root(degree, rightLatex)}}`
    : `${root(degree, leftLatex)}\\cdot ${root(degree, rightLatex)}`;
  const combined = !right ? left : quotient ? fraction(left.num * right.den, left.den * right.num) : fraction(left.num * right.num, left.den * right.den);
  return { type, data: { degree, left, right, leftLatex, rightLatex, quotient, power, combined, result, expressionLatex }, tags: ["pierwiastki", "ułamki", "e8"] };
}
export const generateTasks = () => TYPES.map(type => generateTask({ type }));
export function renderTask({ data }) {
  return <div><p>Oblicz. Wynik zapisz jako liczbę całkowitą lub skrócony ułamek.</p><BlockMath math={data.expressionLatex} /></div>;
}
export function renderInput(task, answer, onChange, disabled) { return <FractionInput previewFormatter={exactAnswerPreview} value={answer} onChange={onChange} disabled={disabled} label="wynik" placeholder="np. 3/4" />; }
export function checkAnswer({ data }, answer) {
  return checkExactMathAnswer(answer, data.result);
}
export function renderAnswer({ data }) { return <InlineMath math={frac(data.result)} />; }
export function renderUserAnswer(answer) { const raw = typeof answer === "string" ? answer : answer?.raw; return raw ? (exactAnswerPreview(raw) ? <InlineMath math={exactAnswerPreview(raw)} /> : <span>{raw}</span>) : <i>(brak odpowiedzi)</i>; }
export function renderExplanation({ data: x }) {
  const numericExpression = !x.right ? root(x.degree, frac(x.left)) : x.quotient
    ? `\\frac{${root(x.degree, frac(x.left))}}{${root(x.degree, frac(x.right))}}`
    : `${root(x.degree, frac(x.left))}\\cdot ${root(x.degree, frac(x.right))}`;
  return <div>
    <p>Zamieniamy liczby dziesiętne i mieszane na ułamki zwykłe oraz obliczamy potęgi, jeśli występują.</p>
    <BlockMath math={`${x.expressionLatex} = ${numericExpression}`} />
    <p>{x.power ? "Szukamy liczby, której odpowiednia potęga daje liczbę pod pierwiastkiem." : x.quotient ? "Iloraz pierwiastków tego samego stopnia zapisujemy jako pierwiastek z ilorazu. Mianownik jest dodatni." : "Iloczyn pierwiastków tego samego stopnia zapisujemy jako pierwiastek z iloczynu."}</p>
    <BlockMath math={`${numericExpression} = ${root(x.degree, frac(x.combined))} = ${root(x.degree, `\\left(${frac(x.result)}\\right)^{${x.degree}}`)} = ${frac(x.result)}`} />
    <p>Wynik zapisujemy w najprostszej postaci.</p>
  </div>;
}
const adapter = { generateTask, renderTask, renderInput, checkAnswer, renderAnswer, renderUserAnswer, renderExplanation };
export const registry = Object.fromEntries(TYPES.map(type => [type, adapter]));
